import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@utils/classNames';

type ModalMode = 'center' | 'sheet';

interface ModalShellProps {
  open?: boolean;
  onClose: () => void;
  children: ReactNode;
  mode?: ModalMode;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  lockBodyScroll?: boolean;
  backdropClassName?: string;
  containerClassName?: string;
  panelClassName?: string;
  zIndexBackdrop?: string;
  zIndexContainer?: string;
}

export const ModalShell: React.FC<ModalShellProps> = ({
  open = true,
  onClose,
  children,
  mode = 'center',
  closeOnBackdrop = true,
  closeOnEscape = true,
  lockBodyScroll = true,
  backdropClassName,
  containerClassName,
  panelClassName,
  zIndexBackdrop = 'z-[9100]',
  zIndexContainer = 'z-[9101]',
}) => {
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, onClose]);

  useEffect(() => {
    if (!open || !lockBodyScroll) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open, lockBodyScroll]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        className={cn('fixed inset-0 bg-black/70 backdrop-blur-sm', zIndexBackdrop, backdropClassName)}
        onClick={() => closeOnBackdrop && onClose()}
      />
      <div
        className={cn(
          'fixed inset-0 p-4',
          mode === 'center'
            ? 'flex items-center justify-center'
            : 'flex items-end justify-center sm:items-center',
          zIndexContainer,
          containerClassName
        )}
      >
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'w-full bg-dark-secondary border border-white/10 shadow-2xl',
            mode === 'center'
              ? 'max-h-[90dvh] rounded-xl overflow-hidden'
              : 'max-h-[90dvh] rounded-t-2xl sm:rounded-xl overflow-hidden',
            panelClassName
          )}
        >
          {children}
        </div>
      </div>
    </>,
    document.body
  );
};

